// 以下の特徴を持つ DateAdapter
// ・ YYYY-MM-DD 形式でデータを持つ
// ・ Datepicker のフォーマットは moment の locale から取得。
//
// ＜参考＞
// https://gist.github.com/wvanderdeijl/0528e45bd363bce04d3da6e4e0bef753
// https://github.com/angular/components/blob/master/src/material-moment-adapter/adapter/moment-date-adapter.ts

import {DateAdapter, MAT_DATE_LOCALE} from '@angular/material/core';
import {Inject, Injectable, Optional} from '@angular/core';
import * as moment from 'moment';
import {Moment} from 'moment';

/** Creates an array and fills it with values. */
const range = <T>(length: number, valueFunction: (index: number) => T): T[] => {
  const valuesArray = Array(length);
  for (let i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
};

@Injectable()
export class StringDateAdapter extends DateAdapter<string> {


  private localeData!: { // constructor 内でセットされるので ! を付ける。
    firstDayOfWeek: number;
    longMonths: string[];
    shortMonths: string[];
    dates: string[];
    longDaysOfWeek: string[];
    shortDaysOfWeek: string[];
    narrowDaysOfWeek: string[];
  };

  constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
    super();
    this.setLocale(dateLocale || moment.locale());
  }

  invalid(): string {
    return '';
  }

  toIso8601(date: string): string {
    return date;
  }

  override setLocale(locale: string) {
    super.setLocale(locale);

    const momentLocaleData = moment.localeData(locale);
    this.localeData = {
      firstDayOfWeek: momentLocaleData.firstDayOfWeek(),
      longMonths: momentLocaleData.months(),
      shortMonths: momentLocaleData.monthsShort(),
      dates: range(31, (i) => this.createMoment(2017, 0, i + 1).format('D')),
      longDaysOfWeek: momentLocaleData.weekdays(),
      shortDaysOfWeek: momentLocaleData.weekdaysShort(),
      narrowDaysOfWeek: momentLocaleData.weekdaysMin(),
    };
  }

  getYear(date: string): number {
    return parseInt(date.split('-')[0], 10);
  }

  getMonth(date: string): number {
    return parseInt(date.split('-')[1], 10) - 1;
  }

  getDate(date: string): number {
    return parseInt(date.split('-')[2], 10);
  }

  getDayOfWeek(date: string): number {
    return this.toNativeDate(date).getDay();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    // Moment.js doesn't support narrow month names, so we just use short if narrow is requested.
    return style === 'long' ? this.localeData.longMonths : this.localeData.shortMonths;
  }

  getDateNames(): string[] {
    return this.localeData.dates;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return this.localeData.longDaysOfWeek;
    }
    if (style === 'short') {
      return this.localeData.shortDaysOfWeek;
    }
    return this.localeData.narrowDaysOfWeek;
  }

  getYearName(date: string): string {
    return String(this.getYear(date));
  }

  getFirstDayOfWeek(): number {
    return this.localeData.firstDayOfWeek;
  }

  getNumDaysInMonth(date: string): number {
    const d = this.toNativeDate(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  }

  clone(date: string): string {
    return this.createDate(this.getYear(date), this.getMonth(date), this.getDate(date));
  }

  createDate(year: number, month: number, date: number): string {
    return [year, month + 1, date].map(n => String(n)).map(n => n.length < 2 ? `0${n}` : n).join('-');
  }

  today(): string {
    const now = new Date();
    return this.createDate(now.getFullYear(), now.getMonth(), now.getDate());
  }

  parse(value: any, parseFormat: any): string | null {
    if (!value || typeof value !== 'string') { return null; }
    const [d, m, y] = value.split(/[-/]/).map(s => parseInt(s, 10));
    return this.createDate(y, m - 1, d);
  }

  format(date: string, displayFormat: any): string {
    if (!this.isValid(date)) {
      throw Error(`StringDateAdapter: invalid date ${date}`);
    }
    const dtf = new Intl.DateTimeFormat(this.locale, displayFormat);
    return dtf.format(this.toNativeDate(date));
  }

  addCalendarYears(date: string, years: number): string {
    return this.createDateWithOverflow(this.getYear(date) + years, this.getMonth(date), this.getDate(date));
  }

  addCalendarMonths(date: string, months: number): string {
    return this.createDateWithOverflow(this.getYear(date), this.getMonth(date) + months, this.getDate(date));
  }

  addCalendarDays(date: string, days: number): string {
    return this.createDateWithOverflow(this.getYear(date), this.getMonth(date), this.getDate(date) + days);
  }

  getISODateString(date: string): string {
    return this.toNativeDate(date).toISOString();
  }

  isDateInstance(obj: any): boolean {
    return typeof obj === 'string' && !!obj;
  }

  isValid(date: string): boolean {
    if (!/\d{4}-\d{2}-\d{2}/.test(date)) { return false; }
    const [y, m, d] = date.split('-').map(x => parseInt(x, 10));
    if (y < 1800 || y > 3000) { return false; }
    if (m < 1 || m > 12) { return false; }
    if (d < 1 || d > 31) { return false; }
    if ([4, 6, 9, 11].indexOf(m) !== -1 && d > 30) { return false; }
    if (m === 2 && d > 29) { return false; }
    return true;
  }

  private toNativeDate(date: string) {
    return new Date(this.getYear(date), this.getMonth(date), this.getDate(date));
  }

  private createDateWithOverflow(year: number, month: number, date: number): string {
    // convert to Date and back to support things like 2018-01-32 to convert to 2018-02-01
    const d = new Date(year, month, date);
    return [d.getFullYear(), d.getMonth() + 1, d.getDate()].map(n => String(n)).map(n => n.length < 2 ? `0${n}` : n).join('-');
  }

  private createMoment(year: number, month: number, date: number): Moment {
    // Moment.js will create an invalid date if any of the components are out of bounds, but we
    // explicitly check each case so we can throw more descriptive errors.
    if (month < 0 || month > 11) {
      throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
    }

    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }

    const result = moment({year, month, date}).locale(this.locale);

    // If the result isn't valid, the date must have been out of bounds for this month.
    if (!result.isValid()) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }

    return result;
  }

}
